import { CheckCircleFilled, PictureOutlined, StarFilled } from '@ant-design/icons';
import { Button, Card, Col, Empty, Image, message, Row, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import { videoService } from 'service/api/videoService';
import { VideoThumbnailType } from 'type/videoThumbnailType';

const { Text } = Typography;

interface Props {
  thumbnails: VideoThumbnailType[];
  onChanged: () => Promise<void>;
}

/**
 * BEE-5533: 변환 시 자동으로 고른 인물 장면 썸네일(자동 선택 1장 + 추천 3장)을 보여주고
 * 그중 하나(또는 자동 이전의 기본 썸네일)를 기본 썸네일로 지정한다.
 */
const AutoThumbnailCandidates = ({ thumbnails, onChanged }: Props) => {
  const [savingId, setSavingId] = useState<number | null>(null);

  const candidates = thumbnails
    .filter(t => t.source === 'auto')
    .sort((a, b) => (a.candidate_rank ?? 99) - (b.candidate_rank ?? 99));
  // 자동 후보가 아닌 기본 썸네일(수동·MediaHold 등)도 같이 보여 되돌릴 수 있게 한다
  const otherDefault = thumbnails.find(t => t.is_default && t.source !== 'auto');

  const setDefault = async (thumb: VideoThumbnailType) => {
    try {
      setSavingId(thumb.id);
      await videoService.setDefaultThumbnail(thumb.id);
      message.success('Default thumbnail changed');
      await onChanged();
    } catch (error) {
      console.error('Set default thumbnail error:', error);
      message.error('Failed to change default thumbnail');
    } finally {
      setSavingId(null);
    }
  };

  const label = (t: VideoThumbnailType) => {
    if (t.source !== 'auto') return <Tag>Previous</Tag>;
    if (t.candidate_rank === 1)
      return (
        <Tag color="gold" icon={<StarFilled />}>
          Auto pick
        </Tag>
      );
    return <Tag color="blue">Suggestion {(t.candidate_rank ?? 1) - 1}</Tag>;
  };

  const items = otherDefault ? [otherDefault, ...candidates] : candidates;

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <PictureOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <span>Auto Thumbnail Suggestions</span>
        </div>
      }
    >
      {candidates.length === 0 ? (
        <Empty
          description="No auto suggestions yet. They are created when the video is transcoded."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Person scenes picked automatically during transcoding. Choose one to use as the default
            thumbnail.
          </Text>
          <Row gutter={[16, 16]}>
            {items.map(t => (
              <Col xs={24} sm={12} lg={6} key={t.id}>
                <div
                  style={{
                    border: t.is_default ? '2px solid #52c41a' : '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 8,
                  }}
                >
                  <Image
                    src={t.url}
                    alt={`thumbnail ${t.id}`}
                    style={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 8,
                    }}
                  >
                    {label(t)}
                    {t.is_default ? (
                      <Text type="success">
                        <CheckCircleFilled /> Current default
                      </Text>
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        loading={savingId === t.id}
                        disabled={savingId !== null && savingId !== t.id}
                        onClick={() => setDefault(t)}
                      >
                        Set as default
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Card>
  );
};

export default AutoThumbnailCandidates;
